import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { Cell, Pie, PieChart } from "recharts"

import type { DashboardUseCase } from "../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { FEASIBILITY_MAP } from "../../types/use-case.types"

type FeasibilityDonutChartProps = {
  useCases: DashboardUseCase[]
}

export function FeasibilityDonutChart({ useCases }: FeasibilityDonutChartProps) {
  const { t } = useTranslation()

  const chartConfig = useMemo(
    () =>
      ({
        value: { label: t("useCases.dashboard.charts.count") },
      }) satisfies ChartConfig,
    [t]
  )

  const { data, total } = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const uc of useCases) {
      const label = FEASIBILITY_MAP[uc.feasV].label
      counts[label] = (counts[label] || 0) + 1
    }
    return {
      data: Object.entries(counts).map(([name, value]) => {
        const entry = Object.values(FEASIBILITY_MAP).find((f) => f.label === name)
        return {
          name,
          value,
          color: entry?.color ?? "hsl(var(--muted-foreground))",
        }
      }),
      total: useCases.length,
    }
  }, [useCases])

  if (data.length === 0) return null

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("useCases.dashboard.charts.feasibilityDistribution")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <ChartContainer className="h-[200px] w-[60%]" config={chartConfig}>
            <PieChart>
              <Pie
                cx="50%"
                cy="50%"
                data={data}
                dataKey="value"
                innerRadius={50}
                nameKey="name"
                outerRadius={80}
                strokeWidth={1}
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color} stroke="hsl(var(--foreground))" />
                ))}
              </Pie>
              <ChartTooltip content={<ChartTooltipContent hideLabel />} />
              <text
                dominantBaseline="middle"
                fill="hsl(var(--foreground))"
                fontSize={24}
                fontWeight="bold"
                textAnchor="middle"
                x="50%"
                y="50%"
              >
                {total}
              </text>
            </PieChart>
          </ChartContainer>
          <div className="flex flex-col gap-2">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2 text-sm">
                <div
                  className="border-foreground size-3 rounded-full border"
                  style={{ backgroundColor: entry.color }}
                />
                <span>
                  {entry.name} ({entry.value})
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
